import { isFunction } from "lodash";
import { useMutation, useQueryClient } from "react-query";
import { t } from "@/i18n-js/instance";
import { useEmbeddableChatApi } from "@/react/hooks/embeddable_chat/useEmbeddableChatsApi";
import {
  usePunditUserContext,
  useSpaceGroupsContext,
  useSpaceResource,
  useSpacesContext,
} from "@circle-react/contexts";
import { reactQueryPost } from "@circle-react/helpers/backendRequestHelpers";
import { isChatSpace } from "@circle-react/helpers/spaceHelpers";
import {
  communityMemberProfileSettingsPath_DEPRECATED,
  internalApi,
  newSessionPath,
} from "@circle-react/helpers/urlHelpers";
import { selectActions } from "@circle-react/hooks/chatsV2/store";
import { useChatStore } from "@circle-react/hooks/chatsV2/store/useChatStore";
import { useChatParticipantsApi } from "@circle-react/hooks/chatsV2/useChatParticipantsApi";
import type { Space } from "@circle-react/types/Space";
import { useToast } from "@circle-react-uikit/ToastV2";

interface UseJoinSpaceProps {
  path?: string;
  setOpenNotificationsDropdown?: (flag: boolean) => void;
  space: Space;
}

export const useJoinSpace = ({
  space,
  setOpenNotificationsDropdown = () => {},
  path,
}: UseJoinSpaceProps) => {
  const { name, id } = space;
  const queryClient = useQueryClient();
  const { currentUser, currentCommunityMember } = usePunditUserContext();
  const { refetch: refetchSpaceResource } = useSpaceResource({
    spaceId: id,
  });
  const { updateChatRoom } = useChatStore(selectActions);
  const {
    actions: { refresh: refreshSpaceGroups },
  } = useSpaceGroupsContext();
  const {
    actions: { refresh: refreshSpaces },
  } = useSpacesContext();

  //Specific to Chat Spaces
  const { refetchChatRoom } = useEmbeddableChatApi(space.chat_room_uuid);
  const { refetchChatParticipants } = useChatParticipantsApi(
    space.chat_room_uuid,
  );

  const { success, error } = useToast();
  const joinSpaceMutation = useMutation(
    () =>
      reactQueryPost(
        path || internalApi.spaceMembers.joinSpace({ spaceId: id }),
      ),
    {
      onSuccess: async () => {
        await refetchSpaceResource();
        void refreshSpaceGroups();
        void refreshSpaces();
        void queryClient.invalidateQueries(["spaces"]);
        if (isChatSpace(space)) {
          try {
            const { data } = await refetchChatRoom();
            const { chat_room: chatRoom } = data || { chat_room: null };
            if (chatRoom) {
              updateChatRoom(chatRoom);
            }
          } catch (error) {
            console.error("Error refetching chat room", error);
          }
          refetchChatParticipants();
        }
        success(t("joined_space", { space_name: space.name }));
        if (!currentCommunityMember?.profile_confirmed_at) {
          window.location.href = communityMemberProfileSettingsPath_DEPRECATED({
            params: { return_to: window.location.href, new_state: true },
          });
        } else {
          isFunction(setOpenNotificationsDropdown) &&
            setOpenNotificationsDropdown(true);
        }
      },
      onError: (e: any) => {
        error(e.message || t("join_space_failed", { space_name: name }));
      },
    },
  );

  const joinSpace = async () => {
    if (!currentUser) {
      window.location.href = newSessionPath();
    } else {
      return joinSpaceMutation.mutateAsync();
    }
  };

  return {
    joinSpace,
    isSubmitting: joinSpaceMutation.isLoading,
  };
};
